import React, { useEffect, useState, useRef } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { ReviewModel } from '../../../models';
import { Star, Search, Quote, User, X, MessageSquareText, Calendar, GraduationCap } from 'lucide-react';

const maskName = (name: string) => {
    if (!name) return '';
    if (name.length <= 1) return name;
    if (name.length === 2) return name[0] + 'O';
    return name[0] + 'O'.repeat(name.length - 2) + name[name.length - 1];
};

const getYoutubeId = (url?: string) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
};

const IntroReviews: React.FC = () => {
    const [reviews, setReviews] = useState<ReviewModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'all' | 'student' | 'parent'>('all');
    const [selectedReview, setSelectedReview] = useState<ReviewModel | null>(null);

    // Infinite Scroll / Lazy Load
    const [visibleCount, setVisibleCount] = useState(6);
    const observerTargetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchReviews = async () => {
            try {
                const q = query(collection(db, 'reviews'), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ReviewModel));
                setReviews(data);
            } catch (error) {
                console.error('Error fetching reviews:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchReviews();
    }, []);

    // Filter reviews
    const filteredReviews = reviews.filter(r => {
        const matchesTab = activeTab === 'all' || r.reviewerRole === activeTab;
        const matchesSearch =
            (r.youtubeUrl || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.school || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.tags || []).some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesTab && matchesSearch;
    });

    const displayedReviews = filteredReviews.slice(0, visibleCount);
    const hasMore = visibleCount < filteredReviews.length;

    useEffect(() => {
        setVisibleCount(6);
    }, [searchTerm, activeTab]);

    useEffect(() => {
        const target = observerTargetRef.current;
        if (!target || !hasMore) return;

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setVisibleCount(prev => prev + 6);
            }
        }, { threshold: 0.1, rootMargin: '100px' });

        observer.observe(target);
        return () => {
            if (target) observer.unobserve(target);
        };
    }, [hasMore, filteredReviews]);

    // Star Renderer Helper
    const renderStars = (rating: number) => {
        return (
            <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        size={16}
                        className={star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-650'}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="animate-in fade-in duration-700 select-none">
            {/* Hero Section */}
            <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-slate-900 to-indigo-950 text-white relative overflow-hidden">
                <div className="max-w-5xl mx-auto text-center space-y-6 relative z-10">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-white/10">
                        Reviews
                    </div>
                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight break-keep">
                        스스로 증명한 배움의 이야기, <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-violet-400">생생한 수강후기</span>
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-400 font-medium max-w-2xl mx-auto break-keep">
                        정성우학원에서 함께 땀 흘리며 성장을 이뤄낸 <br />
                        수강생들과 학부모님들의 정성 어린 목소리를 직접 확인해 보세요.
                    </p>
                </div>
                <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/20 blur-[100px] rounded-full"></div>
            </section>

            {/* Main Content List Section */}
            <section className="py-16 md:py-24 px-4 md:px-8 max-w-5xl mx-auto">
                {/* Search & Tabs Controls */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
                    {/* Sub Tabs */}
                    <div className="flex p-1 bg-slate-800/40 border border-slate-700/30 rounded-2xl w-full md:w-auto justify-start self-start">
                        {(['all', 'student', 'parent'] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-6 py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all ${
                                    activeTab === tab
                                        ? 'bg-indigo-600 text-white shadow-lg'
                                        : 'text-slate-400 hover:text-slate-200'
                                }`}
                            >
                                {tab === 'all' ? '전체 후기' : tab === 'student' ? '학생 후기' : '학부모 후기'}
                            </button>
                        ))}
                    </div>

                    {/* Search Input */}
                    <div className="relative w-full md:w-72">
                        <input
                            type="text"
                            placeholder="작성자, 학교, 과목, 내용 검색"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-slate-800/20 border border-slate-700/50 rounded-xl px-4 py-3 pl-10 text-slate-200 placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 text-sm font-medium transition-all"
                        />
                        <Search className="absolute left-3.5 top-3.5 text-slate-500" size={16} />
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="h-64 rounded-[2.5rem] bg-slate-800/20 border border-slate-800 animate-pulse"></div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredReviews.length === 0 ? (
                            <div className="col-span-full text-center py-20 bg-slate-900/10 rounded-[3rem] border border-dashed border-slate-800/40">
                                <MessageSquareText className="mx-auto text-slate-500 mb-4" size={48} />
                                <p className="text-slate-400 font-bold">등록된 수강후기가 없습니다.</p>
                                <p className="text-xs text-slate-500 font-medium mt-1">학원 관리자 페이지를 통해 수강후기를 등록해주세요.</p>
                            </div>
                        ) : (
                            displayedReviews.map((review) => {
                                const isLong = review.content.length > 120;
                                return (
                                    <div
                                        key={review.id}
                                        className={`relative flex flex-col justify-between p-6 md:p-8 bg-slate-900/40 rounded-[2.5rem] border ${
                                            review.isBest
                                                ? 'border-indigo-500/30 shadow-indigo-950/20 shadow-xl'
                                                : 'border-slate-800/60'
                                        } hover:border-indigo-500/40 hover:shadow-2xl hover:shadow-indigo-950/40 transition-all duration-300 group`}
                                    >
                                        {review.isBest && (
                                            <div className="absolute -top-3 left-8 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 text-[10px] font-black uppercase tracking-wider px-3.5 py-1.5 rounded-full shadow-lg">
                                                BEST 후기
                                            </div>
                                        )}

                                        {/* Quote Mark */}
                                        <div className="absolute top-6 right-6 opacity-5 group-hover:opacity-10 transition-opacity">
                                            <Quote size={56} className="text-white" />
                                        </div>

                                        <div className="space-y-4">
                                            {/* Stars & Rating */}
                                            <div className="flex items-center justify-between">
                                                {renderStars(review.rating)}
                                                <span className={`text-[10px] font-black px-2 py-0.5 rounded-md ${
                                                    review.reviewerRole === 'student'
                                                        ? 'text-indigo-400 bg-indigo-500/10'
                                                        : 'text-emerald-400 bg-emerald-500/10'
                                                }`}>
                                                    {review.reviewerRole === 'student' ? '수강생' : '학부모'}
                                                </span>
                                            </div>

                                            {/* Subject & Video Badge */}
                                            <div className="space-y-1">
                                                <div className="flex flex-wrap gap-2 items-center">
                                                    {review.subject && (
                                                        <span className="text-[11px] font-black text-indigo-400 tracking-wider">
                                                            [{review.subject}]
                                                        </span>
                                                    )}
                                                    {review.youtubeUrl && (
                                                        <span className="inline-flex items-center gap-1 bg-red-500/10 text-red-400 text-[10px] font-bold px-2.5 py-0.5 rounded border border-red-500/20">
                                                            🎥 영상후기
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Youtube Video Thumbnail if available */}
                                            {(() => {
                                                const youtubeId = getYoutubeId(review.youtubeUrl);
                                                return youtubeId ? (
                                                    <div
                                                        onClick={() => setSelectedReview(review)}
                                                        className="relative aspect-video w-full rounded-2xl overflow-hidden border border-slate-800 bg-black flex items-center justify-center cursor-pointer group-hover:border-indigo-500/30 transition-all duration-300"
                                                    >
                                                        <img
                                                            src={`https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`}
                                                            alt="Youtube Thumbnail"
                                                            className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500"
                                                        />
                                                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                                            <div className="w-11 h-11 rounded-full bg-red-600/90 text-white flex items-center justify-center shadow-lg group-hover:bg-red-600 group-hover:scale-110 transition-all">
                                                                <svg className="w-5 h-5 fill-white" viewBox="0 0 24 24">
                                                                    <path d="M8 5v14l11-7z" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : null;
                                            })()}

                                            {/* Content */}
                                            <p className="text-xs sm:text-sm font-medium text-slate-400 leading-relaxed break-all line-clamp-4">
                                                {review.content}
                                            </p>
                                        </div>

                                        {/* Footer Info */}
                                        <div className="mt-6 pt-4 border-t border-slate-800/60 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {review.imageUrl ? (
                                                    <img
                                                        src={review.imageUrl}
                                                        alt="Reviewer"
                                                        loading="lazy"
                                                        decoding="async"
                                                        className="w-9 h-9 rounded-full object-cover border border-slate-700/50"
                                                    />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-slate-400">
                                                        <User size={16} />
                                                    </div>
                                                )}
                                                <div className="text-left">
                                                    <p className="text-xs font-black text-slate-200">
                                                        {maskName(review.studentName)}
                                                    </p>
                                                    <p className="text-[10px] text-slate-550 font-medium mt-0.5">
                                                        {review.school || ''} {review.grade || ''}
                                                    </p>
                                                </div>
                                            </div>

                                            {isLong ? (
                                                <button
                                                    onClick={() => setSelectedReview(review)}
                                                    className="text-[11px] font-black text-indigo-400 hover:text-indigo-300 transition-colors cursor-pointer"
                                                >
                                                    더보기 &gt;
                                                </button>
                                            ) : (
                                                <span className="text-[10px] text-slate-500 font-medium">
                                                    {new Date(review.createdAt).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}

                {/* Infinite Scroll Trigger */}
                {hasMore && (
                    <div ref={observerTargetRef} className="flex justify-center items-center py-10 mt-6">
                        <div className="flex gap-2 items-center text-slate-500 text-xs font-black">
                            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            <span className="ml-2">더 많은 후기 불러오는 중...</span>
                        </div>
                    </div>
                )}
            </section>

            {/* Read More Detail Modal */}
            {selectedReview && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={() => setSelectedReview(null)}
                    ></div>
                    <div className="relative w-full max-w-xl bg-slate-900 border border-slate-800 rounded-[2.5rem] shadow-2xl p-6 sm:p-8 md:p-10 max-h-[85vh] overflow-y-auto animate-in zoom-in-95 duration-300">
                        <button
                            onClick={() => setSelectedReview(null)}
                            className="absolute top-6 right-6 text-slate-400 hover:text-slate-200 transition-colors z-10 cursor-pointer"
                        >
                            <X size={24} />
                        </button>

                        <div className="space-y-6 text-left selection:bg-indigo-500/30">
                            {/* Best Badge & Stars */}
                            <div className="flex items-center gap-3">
                                {selectedReview.isBest && (
                                    <span className="bg-amber-500/10 text-amber-500 text-[10px] font-black px-3 py-1 rounded-full border border-amber-500/20">
                                        BEST 후기
                                    </span>
                                )}
                                {renderStars(selectedReview.rating)}
                            </div>

                            {/* Category info */}
                            <div className="space-y-1">
                                <div className="flex flex-wrap gap-2 items-center">
                                    {selectedReview.subject && (
                                        <span className="text-xs font-black text-indigo-400 tracking-wider">
                                            [{selectedReview.subject}]
                                        </span>
                                    )}
                                    {selectedReview.youtubeUrl && (
                                        <span className="inline-flex items-center gap-1 bg-red-500/10 text-red-400 text-[10px] font-bold px-2 py-0.5 rounded border border-red-500/20">
                                            🎥 영상후기
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Youtube Embed If exists */}
                            {(() => {
                                const youtubeId = getYoutubeId(selectedReview.youtubeUrl);
                                return youtubeId ? (
                                    <div className="aspect-video w-full rounded-2xl overflow-hidden border border-slate-800 bg-black shadow-inner">
                                        <iframe
                                            className="w-full h-full"
                                            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                                            title="YouTube Video Review"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                ) : null;
                            })()}

                            {/* Content Description */}
                            <div className="text-slate-300 font-medium text-sm leading-relaxed whitespace-pre-wrap py-4 border-t border-b border-slate-800">
                                {selectedReview.content}
                            </div>

                            {/* Reviewer Meta info */}
                            <div className="flex items-center justify-between pt-2">
                                <div className="flex items-center gap-3">
                                    {selectedReview.imageUrl ? (
                                        <img
                                            src={selectedReview.imageUrl}
                                            alt="Reviewer"
                                            className="w-10 h-10 rounded-full object-cover border border-slate-700"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400">
                                            <User size={18} />
                                        </div>
                                    )}
                                    <div>
                                        <p className="text-sm font-black text-slate-200 flex items-center gap-1.5">
                                            {maskName(selectedReview.studentName)}
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                                selectedReview.reviewerRole === 'student'
                                                    ? 'text-indigo-400 bg-indigo-500/10'
                                                    : 'text-emerald-400 bg-emerald-500/10'
                                            }`}>
                                                {selectedReview.reviewerRole === 'student' ? '수강생' : '학부모'}
                                            </span>
                                        </p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {selectedReview.school || ''} {selectedReview.grade || ''}
                                        </p>
                                    </div>
                                </div>

                                <div className="text-right">
                                    <span className="text-xs text-slate-500 flex items-center gap-1.5">
                                        <Calendar size={12} />
                                        {new Date(selectedReview.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntroReviews;
